// EventDetail.js
// v1 "Classic refined" redesign — two-column layout (stacked content cards on
// the left, sticky booking rail on the right) with the shared DetailHeader
// identity band and a cover hero above. Reuses existing pieces
// (CategorySelectorGrid, ConfirmedBrands, BookingBubble, SpaceModal,
// SpaceCardCarousel, BrandLoginPrompt, GoogleMap) instead of
// introducing parallel atoms. Page-shell atoms (cards, section
// heads, capacity bar, amenity chip, state/date pills) live in
// components/EventDetail/atoms.js.
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ClipLoader, PuffLoader } from 'react-spinners';
import {
  getEventBySlug,
  getFloorplanAvailability,
  getEventsByPromoterId,
} from '../../redux/actions/events';
import { createBooking, getEventBookings } from '../../redux/actions/bookings';
import { getPromoter, clearPromoter } from '../../redux/actions/promoter';
import api from '../../utils/axiosConfig';
import normalizeEventDate from 'utils/normalizeEventDate';
import { isEventCancelled } from 'utils/eventStatus';
import { rememberPostAuthRedirect } from 'utils/postAuthRedirect';
import { EMAIL_INVITES_ENABLED } from 'utils/features';
import { trackAction } from 'utils/analytics';
import {
  PhotoIcon,
  DocumentTextIcon,
  EnvelopeOpenIcon,
  MegaphoneIcon,
  LanguageIcon,
  MapPinIcon,
  CalendarIcon,
  ArrowsPointingOutIcon,
  BuildingStorefrontIcon,
} from '@heroicons/react/24/outline';
import {
  GoogleMap,
  MarkerF,
  InfoWindowF,
  useJsApiLoader,
} from '@react-google-maps/api';

import { useModal } from '../../context/ModalContext';
import { useAlert } from 'context/AlertContext';
import { getLowestPrice } from 'utils/getLowestSpacePrice';
import { UTILITY_IDS, getUtilityLabel } from 'utils/utilities';
import geocodeLocation from 'utils/getGeocodeLocation';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { mapBookingError } from 'utils/bookingErrorMapper';
import { computeBookingTotal } from 'utils/bookingPricing';

import ModalDialog from 'components/DialogModal';
import CustomMediaModal from 'components/CustomMediaModal';
import CategoryAccordion from 'components/CategoryAccordion';
import DetailHeader, { DetailPill } from 'components/DetailHeader';
import BookingBubble from 'components/BookingBubble';
import SpaceCardCarousel from 'components/SpaceCardCarousel';
import SpaceModal from 'components/SpaceModal';
import SpaceRowList from 'components/SpaceRowList';
import BookingConfirmationModal from 'components/BookingConfirmationModal';
import ConfirmedBrands from 'components/ConfirmedBrands';
import CategorySelectorGrid from 'components/CategorySelectorGrid';
import BrandLoginPrompt from 'components/BrandLoginPrompt';
import ShowMoreButton from 'components/ShowMoreButton';
import ActiveEventsDropdown from 'components/ActiveEventsDropdown';
import usePageMeta from 'hooks/usePageMeta';
import {
  Card,
  DTSectionHead,
  DTInfoTile,
  DTAmenityChip,
  DTCapacityBar,
  DTStateBadge,
} from 'components/EventDetail/atoms';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];

const normalizeUtilityIds = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((u) => (typeof u === 'string' ? u : u?.id ?? u?.slug))
    .filter(Boolean);
};

const norm = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

const slugify = (str) =>
  norm(str)
    .replace(/[^a-z0-9\s_-]+/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');

const titleCaseFromId = (id) =>
  String(id || '')
    .replace(/^custom:/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());

// Try to find a map {customId -> label} in the event payload.
const getCustomLabelMap = (event) => {
  const map = new Map();
  const arr1 = event?.customAmenities;
  if (Array.isArray(arr1)) {
    for (const label of arr1) {
      if (!label) continue;
      map.set(`custom:${slugify(label)}`, label);
    }
  }
  const arr2 =
    event?.utilitiesCustom ||
    event?.customUtilities ||
    event?.utilityLabelsArray;
  if (Array.isArray(arr2)) {
    for (const item of arr2) {
      const id =
        item?.id ||
        item?.slug ||
        (item?.label ? `custom:${slugify(item.label)}` : null) ||
        (item?.name ? `custom:${slugify(item.name)}` : null);
      const label = item?.label || item?.name || item?.title;
      if (id && label) map.set(id, label);
    }
  }
  const obj =
    event?.utilityLabels ||
    event?.customUtilityLabels ||
    event?.utilitiesLabelMap;
  if (obj && typeof obj === 'object') {
    for (const [k, v] of Object.entries(obj)) {
      if (k && v) map.set(k, v);
    }
  }
  return map;
};

const PreservedText = ({ text }) => (
  <div className="whitespace-pre-wrap break-words text-[13.5px] text-gray-700 leading-[1.7]">
    {text || ''}
  </div>
);

const stripDots = (s) => (typeof s === 'string' ? s.replaceAll('.', '') : s);

const fmtMMMYY = (d, locale) =>
  moment(d).isValid()
    ? stripDots(moment(d).locale(locale).format('MMM YY')).toUpperCase()
    : '';

const fmtWeekday = (d, locale) =>
  moment(d).isValid() ? stripDots(moment(d).locale(locale).format('ddd')) : '';

const fmtLongDate = (d, locale) =>
  moment(d).isValid()
    ? stripDots(moment(d).locale(locale).format('D MMM YYYY'))
    : '';

const fmtTime = (t) => (t && typeof t === 'string' ? t : null);

// Returns { rangeLabel, daysCount, daysToStart, isLive, isPast } from a
// normalised event.date payload. Mirrors EventHeader.getDateDisplay so the
// identity strip / cover pill / state badge all agree.
const summarizeDate = (date, locale) => {
  if (!date || !date.type) return null;

  if (date.type === 'fixed') {
    const mStart = moment(date.startDate);
    const mEnd = moment(date.endDate);
    if (!mStart.isValid() || !mEnd.isValid() || mEnd.isBefore(mStart, 'day')) {
      return null;
    }
    const sameMonthYear =
      mStart.year() === mEnd.year() && mStart.month() === mEnd.month();
    const startLbl = fmtMMMYY(mStart, locale);
    const endLbl = fmtMMMYY(mEnd, locale);
    const rangeLabel = sameMonthYear ? startLbl : `${startLbl} – ${endLbl}`;
    const daysCount = Math.max(mEnd.diff(mStart, 'days') + 1, 1);
    const now = moment().startOf('day');
    const daysToStart = mStart.startOf('day').diff(now, 'days');
    const isLive =
      now.isSameOrAfter(mStart.startOf('day')) &&
      now.isSameOrBefore(mEnd.startOf('day'));
    const isPast = mEnd.endOf('day').isBefore(moment());
    return { rangeLabel, daysCount, daysToStart, isLive, isPast };
  }

  if (
    date.type === 'segmented' &&
    Array.isArray(date.days) &&
    date.days.length
  ) {
    const sorted = [...date.days]
      .filter((d) => moment(d?.date).isValid())
      .sort((a, b) => moment(a.date).diff(moment(b.date)));
    if (!sorted.length) return null;
    const first = moment(sorted[0].date);
    const last = moment(sorted[sorted.length - 1].date);
    const sameMonthYear =
      first.year() === last.year() && first.month() === last.month();
    const startLbl = fmtMMMYY(first, locale);
    const endLbl = fmtMMMYY(last, locale);
    const rangeLabel = sameMonthYear ? startLbl : `${startLbl} – ${endLbl}`;
    const daysCount = sorted.length;
    const now = moment().startOf('day');
    const daysToStart = first.startOf('day').diff(now, 'days');
    const isLive = sorted.some((d) => moment(d.date).isSame(now, 'day'));
    const isPast = last.endOf('day').isBefore(moment());
    return { rangeLabel, daysCount, daysToStart, isLive, isPast };
  }
  return null;
};

const EventDetail = () => {
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showLoginModal } = useModal();

  const isMobile = useMediaQuery('(max-width: 1024px)');

  const [viewingOriginal, setViewingOriginal] = useState(false);
  const [locationCoords, setLocationCoords] = useState(null);
  const [activeMarker, setActiveMarker] = useState(false);
  const [showSpaceModal, setShowSpaceModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [modalContent, setModalContent] = useState({ content: '', title: '' });
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [mediaModalImages, setMediaModalImages] = useState([]);
  const [mediaStartIndex, setMediaStartIndex] = useState(0);
  const [isBookingModalOpen, setBookingModalOpen] = useState(false);
  const [bookedPlacementIds, setBookedPlacementIds] = useState([]);
  const [bookingBrands, setBookingBrands] = useState([]);
  const [activeEvents, setActiveEvents] = useState([]);
  const [activeEventsLoading, setActiveEventsLoading] = useState(false);

  const { eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  // Arrived from a promoter's email invite (see API eventInvites service).
  // Gates both the invite banner and remembering the page for after sign-up.
  const fromInvite =
    EMAIL_INVITES_ENABLED && searchParams.get('invite') === '1';
  // Arrived from the promoter's shared "Apply to exhibit" link or QR code
  // (/apply/:eventId, see utils/applyLink.js). Not behind the email-invite
  // switch: it sends nothing, it only changes how this page greets a brand.
  const fromApplyLink = searchParams.get('apply') === '1';
  const bookingRailRef = useRef(null);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { event, loading } = useSelector(
    (state) => state.events.eventDetails || {}
  );
  const promoter = useSelector((state) => state.promoter.promoter);
  const promoterLoading = useSelector((state) => state.promoter.loading);
  const { error: bookingError } = useSelector((state) => state.bookings || {});
  const [pendingBookingErrorAlert, setPendingBookingErrorAlert] =
    useState(false);

  useEffect(() => {
    if (!pendingBookingErrorAlert) return;
    if (!bookingError) return;
    showAlert({
      title: t('alert_dialog.event.booking_error_title'),
      message: mapBookingError(bookingError, t),
      primaryAction: {
        label: t('alert_dialog.event.booking_error_primary_action'),
      },
    });
    setPendingBookingErrorAlert(false);
  }, [pendingBookingErrorAlert, bookingError, showAlert, t]);

  const isBrandUser = user?.role === 'brand';

  // An invited visitor without an account will sign up, confirm their email
  // and log in — possibly in another tab. Remember this page so that flow
  // brings them back here instead of to the homepage.
  useEffect(() => {
    if ((fromInvite || fromApplyLink) && !isAuthenticated && eventSlug) {
      rememberPostAuthRedirect(`/event/${eventSlug}`);
    }
  }, [fromInvite, fromApplyLink, isAuthenticated, eventSlug]);
  const lowestPrice = event?.spaces ? getLowestPrice(event.spaces) : null;

  // One event view per event page, once the event for this URL has loaded.
  const viewedEventId = event?.slug === eventSlug ? event?.id : null;
  useEffect(() => {
    trackAction('event_view', viewedEventId);
  }, [viewedEventId]);

  // Only once the event matching this URL has loaded, so a stale event from
  // the previous page never titles this one.
  const eventLoaded = event?.slug === eventSlug;
  usePageMeta({
    title: eventLoaded
      ? [event.name, event.city].filter(Boolean).join(' — ')
      : undefined,
    description: eventLoaded ? event.shortDescription : undefined,
  });

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: API_KEY,
    version: 'quarterly',
    libraries: LIBRARIES,
  });

  const fetchLang = viewingOriginal ? undefined : i18n.language;

  // State leak guard: when navigating between events, drop any space
  // selection held over from the previous event. Without this, a brand who
  // selects a space on event A and then navigates to event B still has
  // event A's spaceId in state — the source of the SPACE_EVENT_MISMATCH
  // incident.
  useEffect(() => {
    setSelectedPlace(null);
    setShowSpaceModal(false);
    setBookingModalOpen(false);
  }, [eventSlug]);

  // If the selected placement just became occupied after a refresh,
  // drop the selection so the user can't try to submit it.
  useEffect(() => {
    if (!selectedPlace) return;
    const pickedId = selectedPlace.placementId || selectedPlace.id;
    if (pickedId && bookedPlacementIds.includes(pickedId)) {
      setSelectedPlace(null);
    }
  }, [bookedPlacementIds, selectedPlace]);

  const refreshFloorplanAvailability = useCallback(
    async (eventId) => {
      if (!eventId) return [];
      const res = await dispatch(getFloorplanAvailability(eventId));
      const ids = Array.isArray(res?.occupiedPlacementIds)
        ? res.occupiedPlacementIds
        : [];
      setBookedPlacementIds(ids);
      return ids;
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(getEventBySlug(eventSlug, { lang: fetchLang })).then(
      (eventData) => {
        if (!eventData?.id) return;
        // Availability is public — fetch regardless of auth state so
        // anonymous browsers also see correct red/green markers.
        refreshFloorplanAvailability(eventData.id);
        // bookingBrands is still derived from the bookings list (it needs
        // the Brand association). Only fetch this for authed users
        // because /bookings/event/:id requires a token.
        if (isAuthenticated) {
          dispatch(getEventBookings(eventData.id)).then((res) => {
            const list = Array.isArray(res) ? res : res?.data ?? [];
            const confirmed = new Set(['Paid']);
            const seen = new Set();
            const brands = [];
            for (const b of list) {
              if (
                confirmed.has(String(b.status)) &&
                b.Brand &&
                !seen.has(b.Brand.id)
              ) {
                seen.add(b.Brand.id);
                brands.push(b.Brand);
              }
            }
            setBookingBrands(brands);
          });
        }
      }
    );
  }, [
    eventSlug,
    fetchLang,
    isAuthenticated,
    dispatch,
    refreshFloorplanAvailability,
  ]);

  // Promoter — drives the Hosted-by card on the right rail. Mirrors the
  // EventHeader pattern (clear stale on event swap, fetch fresh). Also
  // resets the lazily-fetched "see more events" list so the menu doesn't
  // show events from a previously-viewed promoter.
  const promoterId = event?.promoterId;
  const promoterIdRef = React.useRef(promoterId);
  useEffect(() => {
    promoterIdRef.current = promoterId;
    if (!promoterId) return;
    dispatch(clearPromoter());
    setActiveEvents([]);
    setActiveEventsLoading(false);
    dispatch(getPromoter(promoterId));
    return () => {
      dispatch(clearPromoter());
    };
  }, [promoterId, dispatch]);

  const isEventActive = (ev) => {
    if (!ev?.date) return false;
    const now = moment();
    const { type, endDate, days } = ev.date;
    if (type === 'fixed') {
      if (!endDate) return false;
      return now.isBefore(moment(endDate, 'YYYY-MM-DD'));
    }
    if (type === 'segmented' && Array.isArray(days)) {
      return days.some(({ date }) =>
        moment(date, 'YYYY-MM-DD').isSameOrAfter(now, 'day')
      );
    }
    return false;
  };

  const fetchActiveEvents = () => {
    if (activeEventsLoading || !promoterId) return;
    const fetchForId = promoterId;
    setActiveEventsLoading(true);
    dispatch(getEventsByPromoterId(promoterId))
      .then((res) => {
        if (promoterIdRef.current !== fetchForId) return;
        if (res && Array.isArray(res)) {
          setActiveEvents(res.filter(isEventActive));
        }
      })
      .finally(() => {
        if (promoterIdRef.current === fetchForId) {
          setActiveEventsLoading(false);
        }
      });
  };

  useEffect(() => {
    if (!isLoaded || !event?.location) {
      setLocationCoords(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const coords = await geocodeLocation(event.location);
      if (!cancelled && coords) setLocationCoords(coords);
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, event?.location]);

  // Build modal images: landscape first, then media[], deduped
  const imagesForModal = useMemo(() => {
    const mediaUrls = (event?.media ?? [])
      .map((f) => (typeof f === 'string' ? f : f?.url))
      .filter(Boolean);
    const cover = event?.landscapeImage || null;
    const ordered = cover ? [cover, ...mediaUrls] : mediaUrls;
    const deduped = [];
    const seen = new Set();
    for (const url of ordered) {
      if (url && !seen.has(url)) {
        seen.add(url);
        deduped.push(url);
      }
    }
    return deduped;
  }, [event?.landscapeImage, event?.media]);

  const clamp = (n, min, max) => Math.max(min, Math.min(n, max));

  const dedupeUrls = (arr) => {
    const out = [];
    const seen = new Set();
    for (const url of arr || []) {
      if (url && !seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    }
    return out;
  };

  const openMediaModalAt = (images, startIndex = 0) => {
    const list = dedupeUrls(Array.isArray(images) ? images : []);
    if (!list.length) return;
    setMediaModalImages(list);
    setMediaStartIndex(clamp(startIndex, 0, list.length - 1));
    setShowMediaModal(true);
  };

  const closeMediaModal = () => {
    setShowMediaModal(false);
    setMediaModalImages([]);
    setMediaStartIndex(0);
  };

  const avatarImagesForModal = useMemo(
    () => dedupeUrls([event?.avatar, ...imagesForModal]),
    [event?.avatar, imagesForModal]
  );

  const confirmedBrandsList = useMemo(() => {
    const fromEventBookings = (() => {
      if (!Array.isArray(event?.bookings)) return [];
      const seen = new Set();
      const out = [];
      for (const b of event.bookings) {
        if (String(b?.status) !== 'Paid') continue;
        const brand = b?.Brand;
        if (!brand || seen.has(brand.id)) continue;
        seen.add(brand.id);
        out.push(brand);
      }
      return out;
    })();
    const list =
      (Array.isArray(event?.brandsConfirmed) &&
        event.brandsConfirmed.length > 0 &&
        event.brandsConfirmed) ||
      (Array.isArray(event?.confirmedBrands) &&
        event.confirmedBrands.length > 0 &&
        event.confirmedBrands) ||
      (fromEventBookings.length > 0 && fromEventBookings) ||
      bookingBrands;
    return (list || []).filter(Boolean);
  }, [
    event?.brandsConfirmed,
    event?.confirmedBrands,
    event?.bookings,
    bookingBrands,
  ]);

  const isCancelled = isEventCancelled(event);

  const isEventBookable = () => {
    // A cancelled event keeps its page, but nothing on it is bookable: this
    // drops the capacity bar and the whole booking rail (T2B-420). The API
    // refuses these bookings too ("Event is not accepting bookings"), so this
    // only saves the brand a pointless round trip.
    if (isCancelled) return false;
    if (!event || !event.date) return false;
    const now = moment();
    const { type, startDate, openHours, days } = event.date;
    if (type === 'fixed') {
      if (!startDate) return false;
      const start = moment(
        `${startDate} ${openHours || '00:00'}`,
        'YYYY-MM-DD HH:mm'
      );
      return start.isValid() && now.isBefore(start);
    }
    if (type === 'segmented' && Array.isArray(days) && days.length) {
      const sorted = [...days]
        .filter((d) => d?.date)
        .sort((a, b) => moment(a.date).diff(moment(b.date)));
      const first = sorted[0];
      if (!first) return false;
      const start = moment(
        `${first.date} ${first.openHours || '00:00'}`,
        'YYYY-MM-DD HH:mm'
      );
      return start.isValid() && now.isBefore(start);
    }
    return false;
  };

  const bookableNow = isEventBookable();

  // A logged-in brand who followed an apply link came to book. On phones the
  // booking rail sits below the whole description, so take them to it.
  useEffect(() => {
    if (!fromApplyLink || !isBrandUser || !bookableNow || !event?.id) return;
    if (!window.matchMedia?.('(max-width: 1023px)').matches) return;
    bookingRailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [fromApplyLink, isBrandUser, bookableNow, event?.id]);

  const handleShowMoreModal = (content, title, size = 'sm') => {
    setModalContent({ content, title, size });
    setShowInfoModal(true);
  };
  const handleCloseMoreModal = () => setShowInfoModal(false);
  const handleSpaceModelClose = () => setShowSpaceModal(false);

  const handleBubbleAction = () => {
    setShowSpaceModal(true);
    if (event?.id) refreshFloorplanAvailability(event.id);
  };

  const handlePlaceSelection = (place) => setSelectedPlace(place);

  const handleBookingModalOpen = async () => {
    // Intent to book counts even if a check below stops it.
    trackAction('book_click', event?.id);
    if (event?.isDummy)
      return showAlert({
        title: t('alert_dialog.event.dummy_event_title', 'Example Event'),
        message: t(
          'alert_dialog.event.dummy_event_message',
          'This is an example event and cannot be booked.'
        ),
        primaryAction: { label: t('common.ok', 'OK') },
      });
    if (!selectedPlace)
      return showAlert({
        title: t('alert_dialog.event.select_place_first_title'),
        message: t('alert_dialog.event.select_place_first_message'),
        primaryAction: {
          label: t('alert_dialog.event.select_place_first_primary_action'),
        },
      });

    if (event?.id) {
      const occupied = await refreshFloorplanAvailability(event.id);
      const pickedPlacementId = selectedPlace?.placementId || selectedPlace?.id;
      if (pickedPlacementId && occupied.includes(pickedPlacementId)) {
        setSelectedPlace(null);
        return showAlert({
          title: t('alert_dialog.event.booking_error_title'),
          message: t(
            'booking_errors.placement_just_taken',
            'Esse lugar acabou de ser reservado. Escolha outro.'
          ),
          primaryAction: {
            label: t('alert_dialog.event.booking_error_primary_action'),
          },
        });
      }
    }

    setBookingModalOpen(true);
  };
  const handleBookingModalClose = () => setBookingModalOpen(false);

  const handleBookingConfirmation = async (documentFiles) => {
    if (!selectedPlace)
      return showAlert({
        title: t('alert_dialog.event.no_place_selected_title'),
        message: t('alert_dialog.event.no_place_selected_message'),
        primaryAction: {
          label: t('alert_dialog.event.no_place_selected_primary_action'),
        },
      });

    const isFloorplanPick =
      selectedPlace &&
      typeof selectedPlace.xPercent === 'number' &&
      typeof selectedPlace.yPercent === 'number';

    const spaceId = isFloorplanPick
      ? selectedPlace.spaceTypeId
      : selectedPlace.id;
    const placementId = isFloorplanPick
      ? selectedPlace.placementId || selectedPlace.id
      : null;

    // Pre-submit assertion: the selected space must belong to the current
    // event. Catches regressions of the state leak even if the reset-on-nav
    // guard above gets removed.
    const spaceEventId =
      selectedPlace?.eventId ?? selectedPlace?.EventId ?? null;
    const spaceBelongsToEvent =
      Array.isArray(event?.spaces) &&
      event.spaces.some((s) => s?.id === spaceId);
    const eventMismatch =
      (spaceEventId != null && spaceEventId !== event?.id) ||
      (Array.isArray(event?.spaces) && !spaceBelongsToEvent);

    if (eventMismatch) {
      console.error('[booking] space/event mismatch detected pre-submit', {
        currentEventId: event?.id,
        currentEventSlug: eventSlug,
        spaceId,
        spaceEventId,
        placementId,
      });
      setSelectedPlace(null);
      setBookingModalOpen(false);
      if (event?.id) dispatch(getEventBySlug(eventSlug, { lang: fetchLang }));
      showAlert({
        title: t('alert_dialog.event.booking_error_title'),
        message: t('booking_errors.space_event_mismatch'),
        primaryAction: {
          label: t('alert_dialog.event.booking_error_primary_action'),
        },
      });
      return;
    }

    let bookingPayload;
    if (documentFiles && Object.keys(documentFiles).length > 0) {
      const translatedNames = Object.keys(documentFiles);

      let originalDocNames = translatedNames;
      try {
        const res = await api.get(`/events/by-slug/${eventSlug}`);
        const raw = res.data?.requiredDocuments;
        const parsed = Array.isArray(raw)
          ? raw
          : typeof raw === 'string'
            ? JSON.parse(raw)
            : [];
        if (parsed.length === translatedNames.length) {
          originalDocNames = parsed;
        }
      } catch {
        // Fall back to translated names
      }

      const fd = new FormData();
      fd.append('eventId', event.id);
      fd.append('brandId', user.id);
      fd.append('spaceId', spaceId);
      fd.append('totalAmount', computeBookingTotal(selectedPlace.price));
      if (placementId) fd.append('placementId', placementId);
      fd.append('documentNames', JSON.stringify(originalDocNames));
      translatedNames.forEach((name) =>
        fd.append('documents', documentFiles[name])
      );
      bookingPayload = fd;
    } else {
      bookingPayload = {
        eventId: event.id,
        brandId: user.id,
        spaceId,
        totalAmount: computeBookingTotal(selectedPlace.price),
        placementId,
      };
    }

    dispatch(createBooking(bookingPayload))
      .then((created) => {
        setBookingModalOpen(false);
        setSelectedPlace(null);
        navigate(`/event/${event.slug}`);

        const serverTotal = Number(created?.totalAmount);
        const totalForDisplay = Number.isFinite(serverTotal)
          ? new Intl.NumberFormat(i18n?.language || 'pt-PT', {
              style: 'currency',
              currency: 'EUR',
            }).format(serverTotal)
          : null;

        showAlert({
          type: 'success',
          title: t('alert_dialog.event.booking_success_title'),
          message: totalForDisplay
            ? t('alert_dialog.event.booking_success_message_with_total', {
                total: totalForDisplay,
                defaultValue: `${t(
                  'alert_dialog.event.booking_success_message'
                )} (${totalForDisplay})`,
              })
            : t('alert_dialog.event.booking_success_message'),
          primaryAction: {
            label: t('alert_dialog.event.booking_success_primary_action'),
          },
        });
      })
      .catch((error) => {
        const status = error?.response?.status;
        const code = error?.response?.data?.error;
        if (code === 'SPACE_EVENT_MISMATCH') {
          console.error(
            '[booking] server rejected with SPACE_EVENT_MISMATCH — state leak',
            {
              currentEventId: event?.id,
              currentEventSlug: eventSlug,
              spaceId,
              placementId,
            }
          );
          setSelectedPlace(null);
          if (eventSlug)
            dispatch(getEventBySlug(eventSlug, { lang: fetchLang }));
          showAlert({
            title: t('alert_dialog.event.booking_error_title'),
            message: t('booking_errors.space_event_mismatch'),
            primaryAction: {
              label: t('alert_dialog.event.booking_error_primary_action'),
            },
          });
          return;
        }
        if (status === 409) {
          setBookingModalOpen(false);
          setSelectedPlace(null);
          if (event?.id) refreshFloorplanAvailability(event.id);
          showAlert({
            title: t('alert_dialog.event.booking_error_title'),
            message: t(
              'booking_errors.placement_just_taken',
              'Esse lugar acabou de ser reservado. Escolha outro.'
            ),
            primaryAction: {
              label: t('alert_dialog.event.booking_error_primary_action'),
            },
          });
          return;
        }
        setPendingBookingErrorAlert(true);
      });
  };

  const trimOrEmpty = (v) => (typeof v === 'string' ? v.trim() : v || '');

  const getFullAddressLabel = (ev) => {
    const formatted = trimOrEmpty(ev?.formattedAddress);
    if (formatted) return formatted;
    const parts = [
      ev?.address,
      ev?.neighborhood,
      ev?.city,
      ev?.region,
      ev?.postalCode,
      ev?.country,
    ]
      .map(trimOrEmpty)
      .filter(Boolean);
    if (parts.length) return parts.join(', ');
    return trimOrEmpty(ev?.location);
  };

  // ---- rendering ----------------------------------------------------------

  const renderEventDate = () => {
    const d = normalizeEventDate(event?.date);
    if (!d) return null;
    const locale = i18n?.language || 'en';

    if (d.type === 'fixed' && d.startDate && d.endDate) {
      const mStart = moment(d.startDate);
      const mEnd = moment(d.endDate);
      if (
        !mStart.isValid() ||
        !mEnd.isValid() ||
        mEnd.isBefore(mStart, 'day')
      ) {
        return (
          <div className="text-sm text-red-600">
            {t('event_detail.invalid_dates', 'Invalid event dates')}
          </div>
        );
      }
      const totalDays = Math.max(mEnd.diff(mStart, 'days') + 1, 1);
      return (
        <>
          <div className="grid grid-cols-2 gap-3">
            <DTInfoTile
              icon={<CalendarIcon className="w-4 h-4" />}
              label={t('common.from')}
              value={`${fmtWeekday(mStart, locale)} · ${fmtLongDate(mStart, locale)}`}
              sub={
                fmtTime(d.openHours)
                  ? `${t('common.opens_at')} ${d.openHours}`
                  : null
              }
              tone="paper"
            />
            <DTInfoTile
              icon={<CalendarIcon className="w-4 h-4" />}
              label={t('common.to')}
              value={`${fmtWeekday(mEnd, locale)} · ${fmtLongDate(mEnd, locale)}`}
              sub={
                fmtTime(d.closeHours)
                  ? `${t('common.closes_at')} ${d.closeHours}`
                  : null
              }
              tone="paper"
            />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-ink-strong text-white text-[11px] font-bold tracking-[0.04em]">
              {totalDays} {totalDays === 1 ? t('common.day') : t('common.days')}
            </span>
            {(fmtTime(d.openHours) || fmtTime(d.closeHours)) && (
              <span className="text-[12px] text-gray-500">
                {d.openHours || '--:--'}–{d.closeHours || '--:--'}
              </span>
            )}
          </div>
        </>
      );
    }

    if (d.type === 'segmented' && Array.isArray(d.days) && d.days.length) {
      const sorted = [...d.days]
        .filter((x) => moment(x?.date).isValid())
        .sort((a, b) => moment(a.date).diff(moment(b.date)));
      if (!sorted.length) return null;
      return (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-ink-strong text-white text-[11px] font-bold tracking-[0.04em]">
              {sorted.length}{' '}
              {sorted.length === 1 ? t('common.day') : t('common.days')}
            </span>
          </div>
          <div className="rounded-2xl ring-1 ring-line divide-y divide-line overflow-hidden bg-paper-2 max-h-72 overflow-y-auto">
            {sorted.map((day, idx) => {
              const m = moment(day.date);
              const oh = fmtTime(day.openHours);
              const ch = fmtTime(day.closeHours);
              return (
                <div
                  key={`${day.date}-${idx}`}
                  className="flex items-center justify-between px-4 py-3 bg-white"
                >
                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.12em] font-bold text-gray-400">
                      {fmtWeekday(m, locale)}
                    </div>
                    <div className="text-[13px] font-bold text-ink-strong">
                      {fmtLongDate(m, locale)}
                    </div>
                  </div>
                  <div className="text-[12px] text-gray-600 tabular-nums">
                    {oh || ch ? (
                      <>
                        {oh || '--:--'} <span className="text-gray-300">–</span>{' '}
                        {ch || '--:--'}
                      </>
                    ) : (
                      <span className="text-gray-400">
                        {t('common.hours_tbd')}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      );
    }

    return (
      <div className="text-sm text-gray-500">{t('event_detail.no_dates')}</div>
    );
  };

  const renderMap = () => {
    const mapContainerStyle = {
      width: '100%',
      height: '220px',
      borderRadius: '16px',
    };
    const options = {
      disableDefaultUI: true,
      zoomControl: false,
      streetViewControl: false,
    };
    if (!isLoaded) {
      return (
        <div className="flex justify-center py-4 h-[220px] items-center bg-paper-2 rounded-2xl ring-1 ring-line">
          <PuffLoader color="#FFBD59" loading={!isLoaded} size={40} />
        </div>
      );
    }
    if (!locationCoords) {
      return (
        <div className="flex items-center gap-3 p-4 h-[220px] justify-center bg-paper-2 rounded-2xl ring-1 ring-line">
          <MapPinIcon className="w-9 h-9 text-gray-400 animate-bounce" />
          <div>
            <h3 className="text-md font-semibold text-gray-600">
              {t('event_detail.no_location')}
            </h3>
            <p className="text-gray-500 text-xs">
              {t('event_detail.no_location_message')}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="rounded-2xl overflow-hidden ring-1 ring-line">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={locationCoords}
          zoom={15}
          options={options}
        >
          <MarkerF
            icon={{
              url: event?.avatar,
              scaledSize: new window.google.maps.Size(40, 40),
            }}
            position={locationCoords}
            onMouseEnter={() => setActiveMarker(true)}
            onMouseLeave={() => setActiveMarker(false)}
            onClick={() => setActiveMarker((v) => !v)}
          >
            {activeMarker && (
              <InfoWindowF
                options={{ enableEventPropagation: false }}
                position={locationCoords}
              >
                <div className="flex items-center gap-3 p-2">
                  <img
                    src={event.avatar}
                    alt="Event"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div className="text-sm font-semibold text-gray-900">
                    {event?.name || 'Event location'}
                  </div>
                </div>
              </InfoWindowF>
            )}
          </MarkerF>
        </GoogleMap>
      </div>
    );
  };

  const AmenitiesChips = ({ isModal = false, limit = 9 }) => {
    const locale = i18n?.language || 'en';
    const selectedIds = normalizeUtilityIds(event?.utilities);
    const selectedSet = new Set(selectedIds);

    const base = UTILITY_IDS.map((id) => ({
      id,
      label: getUtilityLabel(id, locale),
      checked: selectedSet.has(id),
    }));

    const customIds = selectedIds.filter((id) => id.startsWith('custom:'));
    const customLabelMap = getCustomLabelMap(event);
    const customs = customIds.map((id) => ({
      id,
      label: customLabelMap.get(id) || titleCaseFromId(id),
      checked: true,
    }));

    const allById = new Map();
    [...base, ...customs].forEach((item) => allById.set(item.id, item));
    const all = Array.from(allById.values());

    const sorted = useMemo(() => {
      const collator = new Intl.Collator(locale, { sensitivity: 'base' });
      const byChecked = (a, b) =>
        a.checked === b.checked ? 0 : a.checked ? -1 : 1;
      const byLabel = (a, b) => collator.compare(a.label, b.label);
      return [...all].sort((a, b) => {
        const c = byChecked(a, b);
        return c !== 0 ? c : byLabel(a, b);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locale, JSON.stringify(all.map((x) => [x.id, x.checked]))]);

    const shown = isModal ? sorted : sorted.slice(0, limit);
    // The modal is the full list, so it gets more columns as the screen
    // allows and never cuts a label short.
    return (
      <div
        className={`grid gap-2 ${
          isModal
            ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
            : 'grid-cols-2 sm:grid-cols-3'
        }`}
      >
        {shown.map(({ id, label, checked }) => (
          <DTAmenityChip
            key={id}
            label={label}
            checked={checked}
            wrap={isModal}
          />
        ))}
      </div>
    );
  };

  const RegulatoryDocumentLink = ({ url }) => {
    if (!url || typeof url !== 'string' || !url.trim()) return null;
    const cleanUrl = url.trim();
    const fileName = (() => {
      try {
        const noQuery = cleanUrl.split('?')[0];
        return decodeURIComponent(noQuery.split('/').pop() || 'Document.pdf');
      } catch {
        return 'Document.pdf';
      }
    })();
    return (
      <a
        href={cleanUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 rounded-2xl bg-paper-2 ring-1 ring-line hover:ring-amber-300 transition w-full"
      >
        <div className="w-11 h-11 rounded-xl bg-white ring-1 ring-line grid place-items-center text-gray-500">
          <DocumentTextIcon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold text-ink-strong truncate">
            {fileName}
          </div>
          <div className="text-[11px] text-gray-500">PDF</div>
        </div>
        <span className="text-[12px] font-bold text-ink-strong shrink-0">
          {t('common.open', 'Open')} →
        </span>
      </a>
    );
  };

  if (loading || !event) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex flex-col items-center justify-center px-4">
        <ClipLoader color="#FFBD59" loading size={40} />
        <h3 className="mt-3 text-md font-semibold text-gray-600">
          {t('event_detail.event_loading')}
        </h3>
      </div>
    );
  }

  const dateInfo = summarizeDate(
    normalizeEventDate(event.date),
    i18n?.language || 'en'
  );
  const sold =
    Number.isFinite(event.bookedSpaces) &&
    Number.isFinite(event.totalSpaces) &&
    event.totalSpaces > 0 &&
    event.bookedSpaces >= event.totalSpaces;
  // "Sold out" is an availability signal, and availability is signed-in only
  // across the app, so guests get the plain date/live badge instead.
  const showSold = isAuthenticated && sold;
  const fullAddress = getFullAddressLabel(event);
  const hasSubcategories =
    Array.isArray(event?.categoriesAllowed) &&
    event.categoriesAllowed.some((c) => {
      const micros = c?.microCategory ?? c?.microCategoryIds;
      return Array.isArray(micros) && micros.length > 0;
    });
  const spotsOpen =
    Number.isFinite(event.totalSpaces) && Number.isFinite(event.bookedSpaces)
      ? Math.max(event.totalSpaces - event.bookedSpaces, 0)
      : null;
  const canOpenAvatar = Boolean(event?.avatar);

  return (
    <>
      <div
        className={`w-full bg-gray-100 ${isMobile ? 'overflow-x-hidden' : ''}`}
      >
        {/* IDENTITY BAND ================================================== */}
        <DetailHeader
          backLabel={t('event_detail.back_to_events', 'Back to events')}
          onBack={() => navigate(-1)}
          typeLabel={t('common.event')}
          tone="event"
          title={event.name}
          avatar={
            <button
              type="button"
              onClick={() =>
                canOpenAvatar && openMediaModalAt(avatarImagesForModal, 0)
              }
              disabled={!canOpenAvatar}
              className="w-24 h-24 sm:w-[140px] sm:h-[140px] rounded-3xl overflow-hidden ring-1 ring-line shrink-0 focus:outline-none focus:ring-2 focus:ring-brand"
              title={canOpenAvatar ? t('common.open', 'Open') : undefined}
            >
              {event.avatar ? (
                <img
                  src={event.avatar}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full bg-white flex items-center justify-center">
                  <BuildingStorefrontIcon className="w-8 h-8 text-gray-300" />
                </div>
              )}
            </button>
          }
          pills={
            <>
              {fullAddress && (
                <DetailPill icon={MapPinIcon} title={fullAddress}>
                  {[event.city, event.country].filter(Boolean).join(', ') ||
                    fullAddress}
                </DetailPill>
              )}
              {dateInfo && (
                <DetailPill icon={CalendarIcon}>
                  {dateInfo.rangeLabel}
                </DetailPill>
              )}
              {!event.landscapeImage && (
                <DTStateBadge
                  daysToStart={dateInfo?.daysToStart}
                  sold={showSold}
                  isLive={dateInfo?.isLive}
                  isCancelled={isCancelled}
                />
              )}
            </>
          }
          categories={event.categoriesAllowed}
        />

        <div className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-7 py-6 space-y-6">
          {/* The API only returns a draft to the promoter who owns it, so
              whoever sees this banner is previewing their own draft. */}
          {String(event?.status || '').toLowerCase() === 'draft' && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-3">
              <DocumentTextIcon className="w-5 h-5 shrink-0 text-gray-500" />
              <p className="flex-1 min-w-[200px] text-[13px] text-ink">
                <span className="font-bold text-ink-strong">
                  {t('event_detail.draft_banner_title')}
                </span>{' '}
                {t('event_detail.draft_banner_body')}
              </p>
              <button
                type="button"
                onClick={() => navigate('/dashboard?tab=my-events&list=drafts')}
                className="text-[13px] font-bold text-ink-strong underline underline-offset-4 hover:text-brand-deep"
              >
                {t('event_detail.draft_banner_cta')}
              </button>
            </div>
          )}

          {(fromInvite || fromApplyLink) && !isAuthenticated && bookableNow && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl bg-brand-soft ring-1 ring-brand/40 px-4 py-3.5">
              {fromInvite ? (
                <EnvelopeOpenIcon className="w-6 h-6 shrink-0 text-brand-deep" />
              ) : (
                <MegaphoneIcon className="w-6 h-6 shrink-0 text-brand-deep" />
              )}
              <div className="flex-1 min-w-[220px]">
                <p className="text-[14px] font-bold text-ink-strong">
                  {fromInvite
                    ? /* Same name the invite email used. */
                      promoter?.companyName || promoter?.username
                      ? t('event_detail.invite_banner_title', {
                          promoter: promoter.companyName || promoter.username,
                        })
                      : t('event_detail.invite_banner_title_generic')
                    : t('event_detail.apply_banner_title')}
                </p>
                <p className="text-[13px] text-ink mt-0.5">
                  {fromInvite
                    ? t('event_detail.invite_banner_body')
                    : t('event_detail.apply_banner_body')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => showLoginModal()}
                  className="px-3.5 py-2 rounded-lg text-[13px] font-bold text-ink-strong hover:bg-white/60"
                >
                  {t('event_detail.invite_banner_login')}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/register?role=brand')}
                  className="px-3.5 py-2 rounded-lg bg-ink text-white text-[13px] font-bold hover:bg-ink-black"
                >
                  {t('event_detail.invite_banner_signup')}
                </button>
              </div>
            </div>
          )}

          {/* TRANSLATED FROM banner ========================================== */}
          {event.is_translated && event.original_language && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-paper-2 ring-1 ring-line rounded-full px-2.5 py-0.5">
                <LanguageIcon className="w-3.5 h-3.5" />
                {t('event_detail.translated_from', {
                  language:
                    i18n.language === 'pt'
                      ? { pt: 'Português', en: 'Inglês' }[
                          event.original_language
                        ]
                      : { pt: 'Portuguese', en: 'English' }[
                          event.original_language
                        ],
                })}
              </span>
              <button
                type="button"
                onClick={() => setViewingOriginal((v) => !v)}
                className="text-xs font-bold text-ink-strong hover:underline"
              >
                {viewingOriginal
                  ? t('event_detail.view_translated')
                  : t('event_detail.view_original')}
              </button>
            </div>
          )}

          {/* COVER HERO ====================================================== */}
          {event.landscapeImage && (
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-line shadow-card">
              <button
                type="button"
                onClick={() => openMediaModalAt(imagesForModal, 0)}
                className="w-full block focus:outline-none focus:ring-2 focus:ring-brand"
                title={t('common.open', 'Open')}
              >
                <img
                  src={event.landscapeImage}
                  alt=""
                  className="w-full h-[220px] sm:h-[300px] lg:h-[340px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/15 pointer-events-none" />
              </button>
              <div className="absolute top-4 left-4">
                <DTStateBadge
                  daysToStart={dateInfo?.daysToStart}
                  sold={showSold}
                  isLive={dateInfo?.isLive}
                  isCancelled={isCancelled}
                />
              </div>
              {imagesForModal.length > 1 && (
                <button
                  type="button"
                  onClick={() => openMediaModalAt(imagesForModal, 0)}
                  className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-3 h-9 rounded-full bg-white/95 backdrop-blur ring-1 ring-black/5 text-ink-strong text-[12px] font-bold shadow-sm hover:bg-white"
                >
                  <PhotoIcon className="w-4 h-4" />
                  {t('event_detail.all_photos', {
                    count: imagesForModal.length,
                    defaultValue: `All ${imagesForModal.length} photos`,
                  })}
                </button>
              )}
            </div>
          )}

          {/* BODY ============================================================ */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* LEFT — content cards ======================================== */}
            <div className="lg:col-span-2 space-y-5">
              {/* About */}
              <Card>
                <DTSectionHead
                  eyebrow={t('event_detail.about_eyebrow', 'About')}
                  title={t('event_detail.about')}
                />
                <PreservedText text={event.shortDescription} />
                {event.longDescription && (
                  <div className="mt-3 flex justify-end">
                    <ShowMoreButton
                      label={t('common.show_more')}
                      onClick={() =>
                        handleShowMoreModal(
                          <PreservedText text={event.longDescription} />,
                          t('event_detail.about')
                        )
                      }
                    />
                  </div>
                )}
              </Card>

              {/* When */}
              <Card>
                <DTSectionHead
                  eyebrow={t('event_detail.when_eyebrow', 'When')}
                  title={t('event_detail.when')}
                />
                {event?.date ? (
                  renderEventDate()
                ) : (
                  <div className="text-sm text-gray-500">
                    {t('event_detail.no_dates')}
                  </div>
                )}
              </Card>

              {/* Where */}
              <Card>
                <DTSectionHead
                  eyebrow={t('event_detail.where_eyebrow', 'Where')}
                  title={fullAddress || t('event_detail.where')}
                />
                {renderMap()}
              </Card>

              {/* Category */}
              <Card>
                <DTSectionHead
                  eyebrow={t(
                    'event_detail.event_category_eyebrow',
                    'Event category'
                  )}
                  title={t('event_detail.event_category')}
                />
                <CategorySelectorGrid
                  key={`cats-${event.id}`}
                  noTitle
                  initialCategories={event.categoriesAllowed}
                  restrictToSelected
                  readOnly
                  showMicros={false}
                />
                {hasSubcategories && (
                  <div className="mt-3 flex justify-end">
                    <ShowMoreButton
                      label={t('event_detail.show_subcategories')}
                      onClick={() =>
                        handleShowMoreModal(
                          <CategoryAccordion
                            categories={event.categoriesAllowed}
                          />,
                          t('event_detail.event_category')
                        )
                      }
                    />
                  </div>
                )}
              </Card>

              {/* In this event — amenities */}
              <Card>
                <DTSectionHead
                  eyebrow={t('event_detail.in_event_eyebrow', 'In this event')}
                  title={t('event_detail.in_event')}
                />
                <AmenitiesChips />
                <div className="mt-3 flex justify-end">
                  <ShowMoreButton
                    label={t('common.show_more')}
                    onClick={() =>
                      handleShowMoreModal(
                        <AmenitiesChips isModal />,
                        t('event_detail.in_event'),
                        'lg'
                      )
                    }
                  />
                </div>
              </Card>

              {/* Plans / spaces — auth only */}
              {isAuthenticated && (
                <Card>
                  <DTSectionHead
                    eyebrow={t('event_detail.plans_eyebrow', 'Plans')}
                    title={t('event_detail.plans')}
                    action={
                      spotsOpen != null && (
                        <span className="text-[11.5px] text-gray-500">
                          {Array.isArray(event.spaces)
                            ? `${event.spaces.length} plans`
                            : ''}
                          {Array.isArray(event.spaces) && spotsOpen != null
                            ? ' · '
                            : ''}
                          {spotsOpen != null
                            ? `${spotsOpen} ${t('event_detail.spots_open', 'spots open')}`
                            : ''}
                        </span>
                      )
                    }
                  />
                  <SpaceCardCarousel spaces={event.spaces} />
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="text-[12px] text-gray-500">
                      {event.floorplanUrl
                        ? t(
                            'event_detail.floorplan_hint',
                            'See exact positions on the floorplan and pick a spot.'
                          )
                        : t(
                            'event_detail.no_floorplan_hint',
                            'No floorplan for this event — pick a plan from the cards above.'
                          )}
                    </div>
                    <div className="flex items-center gap-2">
                      <ShowMoreButton
                        label={t('event_detail.more_details')}
                        onClick={() =>
                          handleShowMoreModal(
                            <SpaceRowList spaces={event.spaces} />,
                            t('event_detail.plans'),
                            'md'
                          )
                        }
                      />
                      {event.floorplanUrl && isBrandUser && (
                        <button
                          type="button"
                          onClick={() => handleBubbleAction('floorplan')}
                          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-ink-strong text-white text-[12px] font-bold hover:bg-black"
                        >
                          <ArrowsPointingOutIcon className="w-3.5 h-3.5" />
                          {t('event_detail.open_floorplan', 'Open floorplan')}
                        </button>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Regulatory doc */}
              {isAuthenticated && event.regulatoryDocument && (
                <Card>
                  <DTSectionHead
                    eyebrow={t(
                      'event_detail.regulatory_eyebrow',
                      'Regulatory document'
                    )}
                    title={t('event_detail.regulatory_document')}
                  />
                  <RegulatoryDocumentLink url={event.regulatoryDocument} />
                </Card>
              )}

              {/* Confirmed brands */}
              <Card>
                <DTSectionHead
                  eyebrow={t('event_detail.brands_eyebrow', 'Confirmed brands')}
                  title={
                    confirmedBrandsList.length > 0
                      ? `${confirmedBrandsList.length} ${t('event_detail.brands_already_in', 'brands already in')}`
                      : t('event_detail.confirmed_brands')
                  }
                />
                <ConfirmedBrands brands={confirmedBrandsList} />
              </Card>
            </div>

            {/* RIGHT — sticky booking rail ================================ */}
            <div
              ref={bookingRailRef}
              className="lg:col-span-1 mt-6 lg:mt-0 scroll-mt-[80px]"
            >
              <div className="lg:sticky lg:top-[72px] xl:top-[80px] space-y-3">
                {isEventBookable() &&
                  (isAuthenticated && isBrandUser ? (
                    <>
                      <div className="bg-white rounded-3xl ring-1 ring-line shadow-card p-5">
                        <DTCapacityBar
                          current={
                            sold ? event.totalSpaces : event.bookedSpaces || 0
                          }
                          total={event.totalSpaces || 0}
                        />
                      </div>
                      <BookingBubble
                        action={handleBubbleAction}
                        spaceList={event.spaces}
                        isSticky={false}
                        isBrand={isBrandUser}
                        selectedPlace={selectedPlace}
                        lowestPrice={lowestPrice}
                        bookingAction={handleBookingModalOpen}
                        onSelect={handlePlaceSelection}
                        isAuthenticated={isAuthenticated}
                        brands={confirmedBrandsList}
                      />
                    </>
                  ) : (
                    <BrandLoginPrompt
                      brands={confirmedBrandsList}
                      onLogin={() => {
                        trackAction('login_to_book_click', event?.id);
                        showLoginModal();
                      }}
                    />
                  ))}

                {/* Hosted by */}
                <div className="bg-white rounded-3xl ring-1 ring-line p-4">
                  <div className="flex items-center gap-3">
                    {promoterLoading && !promoter ? (
                      <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse shrink-0" />
                    ) : promoter?.avatar ? (
                      <img
                        src={promoter.avatar}
                        alt={promoter?.username || ''}
                        className="w-10 h-10 rounded-full object-cover ring-1 ring-line shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-paper-2 ring-1 ring-line shrink-0 flex items-center justify-center">
                        <PhotoIcon className="w-5 h-5 text-gray-300" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-[10.5px] uppercase font-bold tracking-[0.14em] text-gray-400">
                        {t('event_detail.hosted_by', 'Hosted by')}
                      </div>
                      <div className="text-[13px] font-extrabold text-ink-strong truncate">
                        {promoter?.username || '—'}
                      </div>
                    </div>
                  </div>
                  {promoterId && (
                    <div className="mt-3 pt-3 border-t border-line">
                      <ActiveEventsDropdown
                        activeEvents={activeEvents}
                        loading={activeEventsLoading}
                        onFetch={fetchActiveEvents}
                        user={user}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking confirmation */}
      {isAuthenticated && isBrandUser && (
        <BookingConfirmationModal
          isOpen={isBookingModalOpen}
          onClose={handleBookingModalClose}
          onConfirm={handleBookingConfirmation}
          selectedPlace={selectedPlace}
          user={user}
          event={event}
        />
      )}

      {/* Floorplan modal */}
      <SpaceModal
        spaces={event.spaces}
        floorplanUrl={event.floorplanUrl}
        placements={event.placements}
        selectedPlace={selectedPlace}
        bookedPlacementIds={bookedPlacementIds}
        onPlaceChange={handlePlaceSelection}
        active={showSpaceModal}
        onClose={handleSpaceModelClose}
      />

      {/* Info + Media modals */}
      {showInfoModal && (
        <ModalDialog
          isOpen={showInfoModal}
          closeModal={handleCloseMoreModal}
          title={modalContent.title}
          content={modalContent.content}
          size={modalContent.size}
          hideIcon
        />
      )}
      {showMediaModal && mediaModalImages.length > 0 && (
        <CustomMediaModal
          isOpen={showMediaModal}
          onClose={closeMediaModal}
          images={mediaModalImages}
          initialIndex={mediaStartIndex}
        />
      )}
    </>
  );
};

export default EventDetail;
